// src/hooks/useAuth.ts - UPDATED March 15, 2026
// Added: Multi-tenant support (tenants array from Auth0 claims)
// Added: activeTenant state + setActiveTenant callback
// When a tenant is selected, configures api (V1) and apiV2 with correct URLs.

import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState, useCallback, createContext, useContext, createElement, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TenantClaims, User, TenantBranding, TenantFeatures } from '../types';
import { api } from '../services/api';
import { apiV2 } from '../services/apiV2';
import qboApi from '../services/qboApi';
import { adminApi } from '../services/adminApi';
import { supportApi } from '../services/supportApi';
import { billingApi } from '../services/billingApi';
import type { TenantInfo } from '../components/TenantPicker';
import { hasAdminRole } from '../lib/sysadminRoles';

const CLAIM_NAMESPACE = 'https://genireports.com';

// Multi-tenant admins land on the demo tenant by default every sign-in (they
// can switch from the top-right user menu). Match a known demo slug first, then
// fall back to any tenant whose slug/name mentions "demo".
const DEMO_TENANT_SLUGS = ['dd-demo', 'demo'];
function findDemoTenant(list: TenantInfo[]): TenantInfo | undefined {
  return (
    list.find((t) => DEMO_TENANT_SLUGS.includes(t.slug.toLowerCase())) ??
    list.find((t) => t.slug.toLowerCase().includes('demo') || t.name.toLowerCase().includes('demo'))
  );
}

const DEFAULT_FEATURES: TenantFeatures = {
  hebrewCalendar: true,
  exportExcel: true,
  exportPdf: true,
  arAging: true,
  customerDrilldown: true,
  customReports: false,
  apiAccess: false,
  // Per-tenant display defaults (backward-compatible)
  showCostProfit: true,
  showCasesUnits: false,
  qtyMode: 'units',
  showMargin: true,
  showArBalance: true,
};

const DEFAULT_BRANDING: TenantBranding = {
  company_name: 'Delldata Dashboard',
  primary_color: '#3b82f6',
};

// Internal: the actual auth/tenant state + derivations. Runs ONCE inside
// <AuthProvider> and is shared to the whole tree via context. Consumers call
// useAuthContext() (below), which reads the shared value — so a tenant switch
// in one place (the UserMenu) updates every page, not just that component.
function useProvideAuth() {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  if (auth0.user) console.log('AUTH0_USER_CLAIMS:', JSON.stringify(auth0.user, null, 2));
  const [isReady, setIsReady] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [activeTenant, setActiveTenantState] = useState<TenantInfo | null>(null);

  const isAuth0Configured = Boolean(
    import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID
  );

  // Extract tenants array from Auth0 claims
  const tenants = useMemo<TenantInfo[]>(() => {
    if (!isAuth0Configured || !auth0.user) {
      // Dev mode - single default tenant
      return [{
        slug: 'devash',
        name: 'Delldata Reports',
        api_url: import.meta.env.VITE_API_URL || 'http://localhost:3001',
      }];
    }

    const claimTenants: TenantInfo[] = auth0.user[`${CLAIM_NAMESPACE}/tenants`] || [];

    if (claimTenants.length > 0) return claimTenants;

    // Fallback: build from legacy single-tenant claims
    return [{
      slug: auth0.user[`${CLAIM_NAMESPACE}/tenant_slug`] || 'devash',
      name: auth0.user[`${CLAIM_NAMESPACE}/branding`]?.company_name || 'Delldata Reports',
      api_url: auth0.user[`${CLAIM_NAMESPACE}/api_url`] || import.meta.env.VITE_API_URL || '',
    }];
  }, [auth0.user, isAuth0Configured]);

  // Set active tenant - configures both V1 and V2 API services
  const setActiveTenant = useCallback((tenant: TenantInfo) => {
    // Switching tenants: drop all cached React Query data so every page
    // refetches for the new tenant. The API singletons below now point
    // elsewhere, but query keys aren't all tenant-scoped, so without this the
    // pages would keep showing the previous tenant's cached results.
    if (activeTenant && activeTenant.slug !== tenant.slug) {
      queryClient.clear();
    }
    setActiveTenantState(tenant);

    // V1 + QBO: set the bridge URL directly
    if (tenant.api_url) {
      api.setApiUrl(tenant.api_url);
      qboApi.setApiUrl(tenant.api_url);
    }

    // V2: set the tenant slug (Netlify Functions resolve the agent URL)
    apiV2.setTenantSlug(tenant.slug);

    // System Admin proxy uses the same x-tenant-slug routing as V2.
    adminApi.setTenantSlug(tenant.slug);
    supportApi.setTenantSlug(tenant.slug);
    billingApi.setTenantSlug(tenant.slug);

    // V2 DIRECT: set the agent tunnel URL for direct browser→agent calls
    const agentUrl = tenant.api_url || `https://${tenant.slug}.genovatesolutions.com`;
    apiV2.setAgentBaseUrl(agentUrl);
  }, [activeTenant, queryClient]);

  // Auto-select tenant: single tenant -> that one; multi-tenant admins ->
  // the demo tenant (switch later via the user menu). Only if no demo tenant
  // is present do we fall through to the TenantPicker.
  //
  // Guard: in real Auth0 mode do NOT auto-select before the user is
  // authenticated. Pre-login there is no tenant, and selecting the dev-stub
  // tenant would leak its name onto the LoginPage header. Dev mode (Auth0 not
  // configured) still auto-selects so local runs work without signing in.
  useEffect(() => {
  if (activeTenant || auth0.isLoading || tenants.length === 0) return;
  if (isAuth0Configured && !auth0.isAuthenticated) return;
  if (tenants.length === 1) {
    setActiveTenant(tenants[0]);
    return;
  }
  const demo = findDemoTenant(tenants);
  if (demo) setActiveTenant(demo);
}, [tenants, activeTenant, setActiveTenant, auth0.isLoading, auth0.isAuthenticated, isAuth0Configured]);

  // Extract custom claims from the ID token
  const claims = useMemo<TenantClaims | null>(() => {
    if (!isAuth0Configured || !auth0.user) {
      return {
        tenant_id: 'dev-tenant',
        tenant_slug: activeTenant?.slug || 'devash',
        api_url: activeTenant?.api_url || import.meta.env.VITE_API_URL || 'http://localhost:3001',
        roles: [],
        salesman_code: undefined,
        branding: DEFAULT_BRANDING,
        features: DEFAULT_FEATURES,
        allowed_apps: [],
        firstName: '',
        lastName: '',
      };
    }

    const roles: string[] = auth0.user[`${CLAIM_NAMESPACE}/roles`] || [];
    const allowedApps: string[] = auth0.user[`${CLAIM_NAMESPACE}/allowed_apps`] || [];

    return {
      tenant_id:    auth0.user[`${CLAIM_NAMESPACE}/tenant_id`] || activeTenant?.slug || '',
      tenant_slug:  activeTenant?.slug || auth0.user[`${CLAIM_NAMESPACE}/tenant_slug`] || 'devash',
      api_url:      activeTenant?.api_url || auth0.user[`${CLAIM_NAMESPACE}/api_url`] || import.meta.env.VITE_API_URL || '',
      roles,
      salesman_code: auth0.user[`${CLAIM_NAMESPACE}/salesman_code`],
      branding:     auth0.user[`${CLAIM_NAMESPACE}/branding`] || DEFAULT_BRANDING,
      features:     auth0.user[`${CLAIM_NAMESPACE}/features`] || DEFAULT_FEATURES,
      allowed_apps: allowedApps,
      firstName: auth0.user.given_name || '',
      lastName:  auth0.user.family_name || '',
    };
  }, [auth0.user, isAuth0Configured, activeTenant]);

  // App-shell branding (top-LEFT wordmark + logo, and the login screen). This is
  // the stable product/home brand and must NOT change when a multi-tenant admin
  // switches tenants — the top-RIGHT UserMenu is what reflects the active
  // tenant/account name (it reads activeTenant directly). So this is always the
  // branding claim (the user's home brand), falling back to DEFAULT_BRANDING
  // ("Delldata Dashboard") pre-login or when no claim is set.
  const branding = useMemo<TenantBranding>(() => {
    return claims?.branding || DEFAULT_BRANDING;
  }, [claims]);

  const user = useMemo<User | null>(() => {
    if (!isAuth0Configured) {
      return {
        id: 'dev-user',
        email: 'dev@delldata.com',
        name: 'Developer',
        firstName: 'Developer',
        lastName: '',
        roles: [],
      };
    }

    if (!auth0.user) return null;

    return {
      id:           auth0.user.sub || '',
      email:        auth0.user.email || '',
      name:         auth0.user.name || '',
      firstName:    auth0.user.given_name || auth0.user.name?.split(' ')[0] || '',
      lastName:     auth0.user.family_name || auth0.user.name?.split(' ').slice(1).join(' ') || '',
      picture:      auth0.user.picture,
      roles:        claims?.roles || [],
      salesmanCode: claims?.salesman_code,
    };
  }, [auth0.user, claims, isAuth0Configured]);

  const hasFeature = (feature: keyof TenantFeatures): boolean => {
    const val = claims?.features?.[feature] ?? DEFAULT_FEATURES[feature];
    return Boolean(val);
  };

  const hasRole = (role: string): boolean => {
    const roles = claims?.roles || [];
    // Admin-tier roles (GeniReports Admin / Delldata Admin) short-circuit
    // every role check. See src/lib/sysadminRoles.ts for the canonical list.
    return hasAdminRole(roles) || roles.includes(role);
  };

  const isSalesRep = useMemo(() => {
    return (claims?.roles || []).includes('sales_rep');
  }, [claims]);

  // Set up Auth0 and API token when auth state changes
  useEffect(() => {
    const setup = async () => {
      if (!isAuth0Configured || !auth0.isAuthenticated) {
        setIsReady(true);
        return;
      }

      try {
        const token = await auth0.getAccessTokenSilently();
        setAccessToken(token);
        api.setAuthToken(token);
        apiV2.setAuthToken(token);
        qboApi.setAuthToken(token);
        adminApi.setAuthToken(token);
        supportApi.setAuthToken(token);
        billingApi.setAuthToken(token);

        // If tenant already selected, configure APIs
        if (activeTenant) {
          if (activeTenant.api_url) {
            api.setApiUrl(activeTenant.api_url);
          }
          apiV2.setTenantSlug(activeTenant.slug);
          adminApi.setTenantSlug(activeTenant.slug);
          supportApi.setTenantSlug(activeTenant.slug);
          billingApi.setTenantSlug(activeTenant.slug);
        }
      } catch (err) {
        console.error('Failed to get access token:', err);
      } finally {
        setIsReady(true);
      }
    };

    if (!auth0.isLoading) {
      setup();
    }
  }, [auth0.isAuthenticated, auth0.isLoading, isAuth0Configured, activeTenant]);

  const isAuthenticated = isAuth0Configured ? auth0.isAuthenticated : true;
  const isLoading = isAuth0Configured ? (auth0.isLoading || !isReady) : false;

  const login = () => {
    if (isAuth0Configured) {
      auth0.loginWithRedirect();
    }
  };

  const logout = () => {
    setActiveTenantState(null);
    if (isAuth0Configured) {
      auth0.logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  // Does the user need to pick a tenant?
  const needsTenantSelection = isAuthenticated && tenants.length > 1 && !activeTenant;

  return {
    user,
    branding,
    claims,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasFeature,
    hasRole,
    isSalesRep,
    accessToken,
    // Multi-tenant
    tenants,
    activeTenant,
    setActiveTenant,
    needsTenantSelection,
  };
}

// Shared auth/tenant context. All 26+ consumers read ONE value, so a tenant
// switch propagates to every page (previously each useAuthContext() call had
// its own useState, so a switch only updated the component that made it).
type AuthContextValue = ReturnType<typeof useProvideAuth>;
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useProvideAuth();
  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within <AuthProvider>');
  return ctx;
}